import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Divider,
  Box,
  Button,
} from "@mui/material";
import SoftButton from "components/SoftButton";

function ReceiptDialog({ open, onClose, data }) {
  if (!data) return null;

  const date = new Date(data.date).toLocaleString("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          textAlign: "center",
          fontFamily: '"Courier New", monospace',
          fontWeight: "bold",
          borderBottom: "1px dashed #999",
        }}
      >
        RECEIPT
      </DialogTitle>

      <DialogContent
        sx={{
          fontFamily: '"Courier New", monospace',
          backgroundColor: "#fffef8",
          padding: 2,
        }}
      >
        {/* Store Info */}
        <Box textAlign="center" mb={1}>
          <Typography variant="body1" fontWeight="bold">
            {data.store.name}
          </Typography>
          <Typography variant="body2">{data.store.address}</Typography>
          <Typography variant="body2">{data.store.phone}</Typography>
          <Typography variant="body2">{data.store.email}</Typography>
        </Box>

        <Divider sx={{ borderStyle: "dashed", my: 1 }} />

        {/* Date & Cashier */}
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="body2">Date:</Typography>
          <Typography variant="body2">{date}</Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="body2">Cashier:</Typography>
          <Typography variant="body2">{data.cashier_name}</Typography>
        </Box>

        <Divider sx={{ borderStyle: "dashed", my: 1 }} />

        {/* Items */}
        {data.items.map((item, i) => (
          <Box key={i} display="flex" justifyContent="space-between">
            <Typography variant="body2">
              {item.name} x{item.quantity}
            </Typography>
            <Typography variant="body2">
              £{(item.price * item.quantity).toFixed(2)}
            </Typography>
          </Box>
        ))}

        <Divider sx={{ borderStyle: "dashed", my: 1 }} />

        {/* Total */}
        <Box display="flex" justifyContent="space-between">
          <Typography variant="body1" fontWeight="bold">
            TOTAL
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            £{data.total.toFixed(2)}
          </Typography>
        </Box>

        {/* Payment Info */}
        <Box mt={2}>
          <Typography variant="body2">
            Payment Method: {data.payment.method}
          </Typography>
          <Typography variant="body2">
            Transaction ID: {data.payment.transaction_id}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: "dashed", my: 1 }} />

        {/* Return Policy */}
        <Typography
          variant="caption"
          textAlign="center"
          display="block"
          mt={1}
          color="text.secondary"
        >
          {data.return_policy}
        </Typography>

        <Typography
          variant="caption"
          textAlign="center"
          display="block"
          mt={1}
          color="text.secondary"
        >
          Thank you for shopping with us!
        </Typography>

        <Box textAlign="center" mt={2}>
          <SoftButton
            onClick={onClose}
            variant="outlined"
            size="small"
            color="primary"
          >
            Close
          </SoftButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

/* ✅ PropTypes Validation */
ReceiptDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    date: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        price: PropTypes.number.isRequired,
        quantity: PropTypes.number.isRequired,
      })
    ).isRequired,
    store: PropTypes.shape({
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      phone: PropTypes.string.isRequired,
      address: PropTypes.string.isRequired,
    }).isRequired,
    total: PropTypes.number.isRequired,
    payment: PropTypes.shape({
      method: PropTypes.string.isRequired,
      transaction_id: PropTypes.string.isRequired,
    }).isRequired,
    cashier_name: PropTypes.string.isRequired,
    return_policy: PropTypes.string.isRequired,
  }),
};

/* ✅ Default Props (optional safety) */
ReceiptDialog.defaultProps = {
  data: null,
};

export default ReceiptDialog;
